import { useMemo } from 'react';
import { cropKey, useListingCrops, type ByPart, type ListingCrop } from './listingCropsRun';
import { getSubmodels, useSubmodels } from './listingStore';

// THE FEW LISTING CROPS WORTH KEEPING, per part — out of every one the finding call returned.
//
// A RULE, NOT A CALL. It was a second GPT-6 Astra call per part, over every crop cut out. What it
// weighed is all known without it: which side each photograph shows (Phase 1's answer, see
// viewsOf), how much of the photograph the box covers, and whether the part is really in view —
// which the finding call now says per box (`clear`), being the one call that sees the part in its
// photograph. So: drop what is not clear, keep the largest box of each side, largest first.
//
// DERIVED, NEVER STORED: a re-run of the finding call is a new selection on the next render.

// How many a part keeps: about two to four.
const FEWEST = 2;
const MOST = 4;

// WHICH SIDE EACH PHOTOGRAPH SHOWS, by URL — Phase 1's answer, across every submodel.
export const viewsOf = (docId: string): Map<string, string> => {
  const views = new Map<string, string>();
  for (const s of getSubmodels(docId)) {
    s.gallery.forEach((url, i) => {
      const v = s.galleryViews[i];
      if (v && !views.has(url)) views.set(url, v);
    });
  }
  return views;
};

const area = (c: ListingCrop) => {
  const [x0, y0, x1, y1] = c.rawBbox ?? c.bbox;
  return (x1 - x0) * (y1 - y0);
};

// ONE PART'S KEPT CROPS, as crop keys, best first.
//   1. Diverse sides: the largest box of each side, sides ranked by that box.
//   2. Then, short of FEWEST sides, the next largest boxes whatever their side.
// A photograph whose side is unknown is a side of its own: it cannot be called a repeat.
function selectPart(crops: ListingCrop[], views: Map<string, string>): string[] {
  const shown = crops.filter((c) => !c.off && c.clear !== false).sort((a, b) => area(b) - area(a));
  const sides = new Set<string>();
  const kept: ListingCrop[] = [];
  for (const c of shown) {
    const side = views.get(c.url) || c.url;
    if (sides.has(side)) continue;
    sides.add(side);
    kept.push(c);
  }
  for (const c of shown) {
    if (kept.length >= FEWEST) break;
    if (!kept.includes(c)) kept.push(c);
  }
  return kept.slice(0, MOST).map(cropKey);
}

const selectAll = (byPart: ByPart, views: Map<string, string>): Record<number, string[]> =>
  Object.fromEntries(Object.entries(byPart).map(([at, crops]) => [at, selectPart(crops, views)]));

export function useListingSelection(docId: string): { selected: Record<number, string[]> } {
  const { byPart } = useListingCrops(docId);
  // Subscribed so a re-read of the photographs' sides re-selects.
  const submodels = useSubmodels(docId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selected = useMemo(() => selectAll(byPart, viewsOf(docId)), [docId, byPart, submodels]);
  return { selected };
}

// A part's selected crops, in rank order, out of that part's crops.
export const selectedCrops = (crops: ListingCrop[], keys: string[] | undefined): ListingCrop[] =>
  (keys ?? []).flatMap((k) => crops.filter((c) => cropKey(c) === k).slice(0, 1));

// A crop's place in its part's selection, from 1 — or undefined, where it was not kept.
export const rankIn = (keys: string[] | undefined, crop: ListingCrop): number | undefined => {
  const at = keys?.indexOf(cropKey(crop)) ?? -1;
  return at < 0 ? undefined : at + 1;
};
